import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import DashboardLayout from '../components/DashboardLayout';

interface UnitFormData {
  name: { en: string; ar: string };
  type: string;
  order: number;
  active: boolean;
}

const UNIT_TYPES = ['volume', 'weight', 'length', 'count', 'other'] as const;

export default function UnitForm() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<UnitFormData>({
    name: { en: '', ar: '' },
    type: 'other',
    order: 0,
    active: true,
  });

  useEffect(() => {
    if (isEditing && id) loadUnit();
  }, [id]);

  const loadUnit = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const res = await api.get(`/admin/units/${id}`);
      const u = res.data?.data ?? res.data;
      setFormData({
        name: u.name ?? { en: '', ar: '' },
        type: u.type ?? 'other',
        order: u.order ?? 0,
        active: u.active !== false,
      });
    } catch (err) {
      console.error(err);
      alert(t('unitForm.failedToLoad'));
      navigate('/?tab=units');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      const payload = {
        name: formData.name,
        type: formData.type,
        order: formData.order,
        active: formData.active,
      };
      if (isEditing && id) {
        await api.put(`/admin/units/${id}`, payload);
      } else {
        await api.post('/admin/units', payload);
      }
      navigate('/?tab=units');
    } catch (err: any) {
      console.error(err);
      alert(err.response?.data?.message || t('unitForm.failedToSave'));
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditing) {
    return (
      <DashboardLayout title={t('common.loading')}>
        <div className="flex items-center justify-center min-h-[300px]">{t('unitForm.loading')}</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title={isEditing ? t('unitForm.editUnit') : t('unitForm.createUnit')}
      subtitle={t('unitForm.subtitle')}
    >
      <div className="max-w-2xl mx-auto">
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('unitForm.nameEn')}</label>
            <input
              type="text"
              required
              value={formData.name.en}
              onChange={(e) => setFormData({ ...formData, name: { ...formData.name, en: e.target.value } })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('unitForm.nameAr')}</label>
            <input
              type="text"
              value={formData.name.ar}
              onChange={(e) => setFormData({ ...formData, name: { ...formData.name, ar: e.target.value } })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              dir="rtl"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('unitForm.type')}</label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            >
              {UNIT_TYPES.map((type) => (
                <option key={type} value={type}>{t(`units.${type}`)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('unitForm.order')}</label>
            <input
              type="number"
              min={0}
              value={formData.order}
              onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value, 10) || 0 })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="unit-active"
              checked={formData.active}
              onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
              className="rounded border-gray-300 text-primary-orange"
            />
            <label htmlFor="unit-active" className="text-sm font-medium text-gray-700">{t('unitForm.active')}</label>
          </div>
          <div className="flex flex-wrap justify-end gap-4 pt-4 border-t">
            <button
              type="button"
              onClick={() => navigate('/?tab=units')}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              {t('unitForm.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-primary-orange text-white rounded-lg hover:bg-orange-600 disabled:opacity-50"
            >
              {t('unitForm.save')}
            </button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}
